import React, { useEffect, useState } from 'react'
import { fmt, pickCsvFile } from '../api.js'
import { portal } from '../portalApi.js'
import SearchSelect from '../components/SearchSelect.jsx'
import NoteDrawer from '../components/NoteDrawer.jsx'
import { CompanyPicker, NeedCompany, costGet, useCompany, useMeta, useToast } from './CostCommon.jsx'

// 원가관리 · 제조원가명세서. 단가는 직접귀속분만 넣고, 배부액은 배부기준(원장)에서
// 서버가 계산해 내려준다. 원가 항목은 6개까지 늘어날 수 있어 열을 데이터로 그린다.

export default function CostProduction({ month, onMonthChange, currentUserId, autoOpenNoteId, onAutoOpened }) {
  const [company, setCompany] = useCompany()
  const [meta] = useMeta()
  const [st, setSt] = useState(null)
  const [items, setItems] = useState([])
  const [edits, setEdits] = useState({})   // item_id -> {qty?, <col>?}
  const [newSku, setNewSku] = useState('')
  const [err, setErr] = useState('')
  const [notify, toast] = useToast()

  const load = () => {
    setErr('')
    Promise.all([
      costGet('/cost-statement', { month, company }),
      costGet('/items', { company }),
    ])
      .then(([s, it]) => { setSt(s); setItems(it.filter((i) => i.active)); setEdits({}) })
      .catch((e) => setErr(e.message))
  }
  useEffect(() => { load() }, [month, company])

  const cols = (st?.fields || []).filter((f) => f.used || f.code)

  const save = async () => {
    const byId = {}
    for (const r of st.rows) byId[r.item_id] = r
    const list = Object.entries(edits).map(([itemId, e]) => {
      const base = byId[itemId]
      const row = { company: base.company, month, sku: base.sku, qty: e.qty ?? base.qty }
      for (const f of st.fields) row[f.col] = e[f.col] ?? base.detail[f.field].base_unit
      return row
    })
    if (!list.length) return
    try {
      await portal.post('/api/cost/production/bulk', list)
      notify(`${list.length}건 저장 완료`)
      load()
    } catch (e) { setErr(e.message) }
  }

  const addRow = async () => {
    if (!newSku || !company) return
    try {
      await portal.post('/api/cost/production/bulk', [{ company, month, sku: newSku, qty: 0 }])
      setNewSku('')
      load()
    } catch (e) { setErr(e.message) }
  }

  const removeRow = async (id) => {
    try { await portal.del(`/api/cost/production/${id}`); load() } catch (e) { setErr(e.message) }
  }

  const importCsv = () => pickCsvFile(async (rows) => {
    const list = rows.map((r) => ({ company, month, ...r, month: r.month || month }))
    try {
      const res = await portal.post('/api/cost/production/bulk', list)
      notify(`업로드 ${res.upserted}건 반영${res.missing.length ? `, 미등록 품번 ${res.missing.length}건 제외` : ''}`)
      if (res.missing.length) setErr(`품목 마스터에 없는 품번: ${res.missing.slice(0, 20).join(', ')}${res.missing.length > 20 ? ' 외' : ''}`)
      load()
    } catch (e) { setErr(e.message) }
  })

  if (err && !st) return <div className="error-box">{err}</div>
  if (!st) return <div className="muted">불러오는 중…</div>

  const ev = (r, key, fallback) => edits[r.item_id]?.[key] ?? fallback
  const span = 4 + cols.length * 3 + 3

  return (
    <>
      <div style={{ position: 'relative' }}>
        <h2>{month} 제조원가명세서</h2>
        <NoteDrawer target="production" screenKey="cost_production"
          context={{ month }} contextLabel={month}
          onNavigate={(ctx) => ctx?.month && onMonthChange?.(ctx.month)}
          autoOpenNoteId={autoOpenNoteId} onAutoOpened={onAutoOpened}
          currentUserId={currentUserId} />
      </div>
      {err && <div className="error-box">{err}</div>}
      <div className="row" style={{ marginBottom: 12 }}>
        <CompanyPicker value={company} onChange={setCompany} meta={meta} />
        <SearchSelect
          width={320}
          placeholder="품목 검색 후 행 추가…"
          value={newSku}
          onChange={setNewSku}
          options={items
            .filter((i) => !st.rows.some((r) => r.item_id === i.id))
            .map((i) => ({ value: i.sku, label: `[${i.sku}] ${i.name} ${i.option_name}`.trim() }))}
        />
        <button className="btn secondary" onClick={addRow} disabled={!newSku || !company}>행 추가</button>
        <button className="btn secondary" onClick={importCsv} disabled={!company}>CSV 업로드</button>
        <button className="btn" onClick={save} disabled={!Object.keys(edits).length}>
          변경사항 저장 ({Object.keys(edits).length})
        </button>
      </div>
      {!company && <NeedCompany>통합 화면에서는 두 사업부 명세를 함께 보여줍니다. 행 추가·업로드는 사업부를 골라야 합니다.</NeedCompany>}
      <div className="hint">
        CSV 헤더: <code>sku, qty, {cols.map((c) => c.col).join(', ')}</code> (month 열이 있으면 그 월로, 없으면 현재 기준월로 반영)
        · 단가는 직접귀속 단가입니다. 배부액은 배부기준의 원장 금액에서 자동 계산됩니다.
      </div>
      <div className="panel">
        <table>
          <thead>
            <tr>
              <th rowSpan={2} className="l">품번</th>
              <th rowSpan={2} className="l">상품명</th>
              <th rowSpan={2} className="l">옵션</th>
              <th rowSpan={2}>생산량</th>
              {cols.map((c) => <th key={c.field} colSpan={3}>{c.label}</th>)}
              <th rowSpan={2}>총 제조원가</th>
              <th rowSpan={2}>개당원가</th>
              <th rowSpan={2} />
            </tr>
            <tr>
              {cols.map((c) => (
                <React.Fragment key={c.field}>
                  <th>단가</th><th>직접</th><th>배부</th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {st.rows.length === 0 && <tr><td colSpan={span} className="c muted">이 달의 생산 실적이 없습니다. 행을 추가하거나 CSV를 업로드하세요.</td></tr>}
            {st.rows.map((r) => (
              <tr key={r.item_id}>
                <td className="l">{r.sku}</td>
                <td className="l">{r.name}</td>
                <td className="l">{r.option_name}</td>
                <td>
                  <input value={ev(r, 'qty', r.qty)} onChange={(e) =>
                    setEdits({ ...edits, [r.item_id]: { ...edits[r.item_id], qty: e.target.value } })} />
                </td>
                {cols.map((c) => (
                  <React.Fragment key={c.field}>
                    <td>
                      <input value={ev(r, c.col, r.detail[c.field].base_unit)} onChange={(e) =>
                        setEdits({ ...edits, [r.item_id]: { ...edits[r.item_id], [c.col]: e.target.value } })} />
                      {c.field === 'direct_expense' && r.detail.direct_expense.auto_license && (
                        <div className={r.detail.direct_expense.license_factor === 0 ? 'warn' : 'muted'}
                          style={{ fontSize: 11 }}>
                          {r.detail.direct_expense.license_name} 상각 {fmt(r.detail.direct_expense.unit, 1)}
                          {r.detail.direct_expense.license_factor === 0
                            ? ' (선급금 소진)'
                            : r.detail.direct_expense.license_factor !== 1
                              ? ` (${(r.detail.direct_expense.license_factor * 100).toFixed(0)}%)`
                              : ''}
                        </div>
                      )}
                    </td>
                    <td>{fmt(r.detail[c.field].direct)}</td>
                    <td className={r.detail[c.field].alloc < 0 ? 'warn' : ''}>{fmt(r.detail[c.field].alloc)}</td>
                  </React.Fragment>
                ))}
                <td><b>{fmt(r.totalCost)}</b></td>
                <td>{fmt(r.unitCost, 1)}</td>
                <td className="c"><button className="btn danger" onClick={() => removeRow(r.production_id)}>삭제</button></td>
              </tr>
            ))}
            {st.rows.length > 0 && (
              <tr className="total">
                <td className="l" colSpan={3}>합계</td>
                <td>{fmt(st.totalQty)}</td>
                {cols.map((c) => (
                  <React.Fragment key={c.field}>
                    <td />
                    <td>{fmt(st.totals[c.field].direct)}</td>
                    <td>{fmt(st.totals[c.field].alloc)}</td>
                  </React.Fragment>
                ))}
                <td>{fmt(st.grandTotal)}</td>
                <td>{st.totalQty > 0 ? fmt(st.grandTotal / st.totalQty, 1) : '-'}</td>
                <td />
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {toast}
    </>
  )
}
